import { DocumentItemsValidationsBoPart, DocumentItemsValidationsBoPartModelPart, propertyChangedJoinPointFilter, QuantityLessThanZeroError, QuantityMustBeGreaterThanZeroError } from '@skylab-shared/business';
import { BusinessObjectPartContext, ObservableCollection, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { CalculationMethod, PurchaseDocumentKind, PurchaseInvoiceDocumentItemModel } from '@skylab-shared/dataModel';
import { PurchaseInvoiceItemPriceShouldNotBeLessThanZero } from '../violations/purchase-invoice-item-price-cannot-be-less-than-zero-warning';

interface DocumentModelPart extends DocumentItemsValidationsBoPartModelPart {
    items: ObservableCollection<PurchaseInvoiceDocumentItemModel>;
    kind: PurchaseDocumentKind;
}

export class PurchaseDocumentItemsValidationsBoPart extends DocumentItemsValidationsBoPart<DocumentModelPart> {
    public constructor(
        weaver: Weaver,
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerPriceValidationRules();
        this.registerQuantityValidationRules();
        this.registerReadonlyRules();
    }

    protected registerReadonlyRules(): void {
        this.engine.readonly.setAsReadonly([PurchaseInvoiceDocumentItemModel, 'value_vatAfterDiscount']);
        this.engine.readonly.setAsReadonly([PurchaseInvoiceDocumentItemModel, 'inventoryUnitCost']);
        this.engine.readonly.setAsReadonly([PurchaseInvoiceDocumentItemModel, 'inventoryTotalCost']);
    }

    protected override registerReadonlyOmitRules(): void {
        super.registerReadonlyOmitRules();
        this.engine.readonlyChecker.addOmitRule([
            [PurchaseInvoiceDocumentItemModel, 'inventoryUnitCost'],
            [PurchaseInvoiceDocumentItemModel, 'inventoryTotalCost']
        ]);
    }

    private registerPriceValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod'])(
            () => {
                this.engine.validation.queueValidation([PurchaseInvoiceDocumentItemModel, 'price_grossBeforeDiscount']);
                this.engine.validation.queueValidation([PurchaseInvoiceDocumentItemModel, 'price_netBeforeDiscount']);
            }
        )));

        this.engine.validation.addRule(
            [[PurchaseInvoiceDocumentItemModel, 'price_grossBeforeDiscount']],
            [[PurchaseInvoiceDocumentItemModel, 'price_grossBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Net) return true;
                return ctx.source.price_grossBeforeDiscount >= 0;
            },
            PurchaseInvoiceItemPriceShouldNotBeLessThanZero
        );

        this.engine.validation.addRule(
            [[PurchaseInvoiceDocumentItemModel, 'price_netBeforeDiscount']],
            ctx => {
                if (this.bo.model.calculationMethod === CalculationMethod.Gross) return true;
                return ctx.source.price_netBeforeDiscount >= 0;
            },
            PurchaseInvoiceItemPriceShouldNotBeLessThanZero
        );
    }

    private registerQuantityValidationRules(): void {
        this.engine.validation.addRule(
            [[PurchaseInvoiceDocumentItemModel, 'quantity']],
            ctx => this.bo.model.kind === PurchaseDocumentKind.PurchaseInvoice ? ctx.source.quantity > 0 : true,
            QuantityMustBeGreaterThanZeroError
        );

        this.engine.validation.addRule(
            [[PurchaseInvoiceDocumentItemModel, 'quantity']],
            ctx => this.bo.model.kind === PurchaseDocumentKind.PurchaseInvoiceAdjustment ? ctx.source.quantity >= 0 : true,
            QuantityLessThanZeroError
        );
    }
}
